import { Fragment, useState, useMemo, useCallback } from "react";
import { Check, ChevronsUpDown, Trash2, Plus, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CameraIconPicker, getCameraIcon } from "./CameraIconPicker";
import { EquipmentGroupSelect } from "./CameraEquipmentSelect";
import { CameraEquipmentInline } from "./CameraEquipmentInline";
import {
  SelectFunctionDialog,
  type FunctionOption,
} from "./SelectFunctionDialog";
import {
  useUpdateCamera,
  useDeleteCamera,
  useAddCameraOperator,
  useRemoveCameraOperator,
} from "@/hooks/useCameraPlans";
import {
  useProjectEquipmentGroups,
  useCreateEquipmentGroup,
  useUpdateEquipmentGroup,
  useCreateEquipmentAssignment,
  useUpdateEquipmentAssignment,
  useCreateTemporaryEquipment,
  useDeleteEquipmentAssignment,
  useAssignInstance,
  useUnassignInstance,
} from "@/hooks/useProjectEquipment";
import { useBatchEquipmentInstances } from "@/hooks/useEquipmentInstances";
import {
  getDraftFieldKeyDownHandlers,
  useDraftField,
} from "@/hooks/useDraftField";
import {
  useProjectFunctions,
  useCreateFunctionAssignment,
  useUpdateProjectFunctionDay,
  useCreateProjectFunctionSeries,
  type FunctionAssignment,
  type ProjectFunctionSeries,
  type ProjectFunctionDay,
} from "@/hooks/useProjectPeople";
import {
  useCameraPlanEquipmentColumns,
  type CameraPlanEquipmentColumn,
} from "@/hooks/useCameraPlanEquipmentColumns";
import { usePeople, type PersonWithCompany } from "@/hooks/usePeople";
import { cn } from "@/lib/utils";
import type {
  CameraWithEquipment,
  CameraPlanColumn,
  CameraIcon,
  ZeroDateValue,
} from "@/types/camera-plans";

const TRANSMISSION_OPTIONS = ["SDI", "Fiber", "Video-Link", "Bonding"];
const TRANSMISSION_CUSTOM_VALUE_KEY = "transmission";

interface CameraListViewProps {
  cameras: CameraWithEquipment[];
  columns: CameraPlanColumn[];
  cameraPlanId: string;
  projectId: string;
  eventDate?: ZeroDateValue;
  selectedCameraId?: string | null;
  onSelectCamera?: (id: string | null) => void;
}

export function CameraListView({
  cameras,
  columns,
  cameraPlanId,
  projectId,
  eventDate,
  selectedCameraId,
  onSelectCamera,
}: CameraListViewProps) {
  const { data: equipmentGroups = [] } = useProjectEquipmentGroups(projectId);
  const { data: projectFunctions = [] } = useProjectFunctions(projectId);
  const { columns: equipmentColumns } = useCameraPlanEquipmentColumns();
  const { data: allPeople = [] } = usePeople(["employee", "freelancer"]);
  const normalizedEventDate = useMemo(
    () => zeroDateToDateString(eventDate),
    [eventDate],
  );
  const transmissionColumnIndex = useMemo(
    () => getTransmissionColumnIndex(equipmentColumns),
    [equipmentColumns],
  );

  // Flatten function assignments, filtered to camera_position tagged series
  // and optionally by event date
  const allFunctionAssignments = useMemo(() => {
    const assignments: (FunctionAssignment & {
      functionName: string;
      functionDate: string;
    })[] = [];
    for (const series of projectFunctions) {
      // Only include camera_position tagged functions
      if (series.system_tag !== "camera_position") continue;

      for (const day of series.days) {
        // If event date is set, only include matching date
        if (normalizedEventDate && day.function_date !== normalizedEventDate)
          continue;

        for (const assignment of day.assignments ?? []) {
          assignments.push({
            ...assignment,
            functionName: series.name,
            functionDate: day.function_date,
          });
        }
      }
    }
    return assignments;
  }, [projectFunctions, normalizedEventDate]);

  // Camera_position function days matching the event date (for auto-assignment)
  const cameraPositionDaysForDate = useMemo(() => {
    const results: {
      series: ProjectFunctionSeries;
      day: ProjectFunctionDay;
    }[] = [];
    for (const series of projectFunctions) {
      if (series.system_tag !== "camera_position") continue;
      for (const day of series.days) {
        if (normalizedEventDate && day.function_date !== normalizedEventDate)
          continue;
        results.push({ series, day });
      }
    }
    return results;
  }, [projectFunctions, normalizedEventDate]);

  // Function assignment IDs already linked to ANY camera in this plan
  const allLinkedFaIds = useMemo(() => {
    const ids = new Set<string>();
    for (const camera of cameras) {
      for (const cfa of camera.cameraFunctionAssignments ?? []) {
        ids.add(cfa.functionAssignmentID);
      }
    }
    return ids;
  }, [cameras]);

  // People already assigned to a camera_position function for this date
  const assignedPersonIds = useMemo(() => {
    const ids = new Set<string>();
    for (const fa of allFunctionAssignments) {
      ids.add(fa.person_id);
    }
    return ids;
  }, [allFunctionAssignments]);

  // People not yet assigned to a camera_position function — fallback candidates
  const fallbackPeople = useMemo(
    () => allPeople.filter((p) => !assignedPersonIds.has(p.id)),
    [allPeople, assignedPersonIds],
  );

  const addOperator = useAddCameraOperator();
  const removeOperator = useRemoveCameraOperator();
  const createFunctionAssignment = useCreateFunctionAssignment();
  const updateFunctionDay = useUpdateProjectFunctionDay();
  const createFunctionSeries = useCreateProjectFunctionSeries();

  // Dialog state for function selection when auto-assignment isn't possible
  const [functionDialogState, setFunctionDialogState] = useState<{
    personId: string;
    personName: string;
    cameraId: string;
    options: FunctionOption[];
  } | null>(null);
  const createGroup = useCreateEquipmentGroup();
  const createAssignment = useCreateEquipmentAssignment();
  const updateAssignment = useUpdateEquipmentAssignment();
  const createTemporary = useCreateTemporaryEquipment();
  const deleteAssignment = useDeleteEquipmentAssignment();
  const assignInstance = useAssignInstance();
  const unassignInstance = useUnassignInstance();
  const updateCamera = useUpdateCamera();

  // Build a map of equipment_id → tag IDs from group assignments (avoids
  // pagination issues with the global equipment list)
  const equipmentTagMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of equipmentGroups) {
      for (const a of group.assignments) {
        if (a.equipment_id && a.equipment?.equipment_tag_assignments) {
          map.set(
            a.equipment_id,
            a.equipment.equipment_tag_assignments.map((t) => t.tag_id),
          );
        }
      }
    }
    return map;
  }, [equipmentGroups]);

  // Batch-fetch instances for all serialized equipment in groups
  const serializedEquipmentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of equipmentGroups) {
      for (const a of group.assignments) {
        if (a.equipment_id && a.equipment?.is_serialized) {
          ids.add(a.equipment_id);
        }
      }
    }
    return Array.from(ids);
  }, [equipmentGroups]);

  const { data: instancesByEquipmentId = {} } = useBatchEquipmentInstances(
    serializedEquipmentIds,
  );

  const handleAutoCreateGroup = useCallback(
    (cameraId: string, cameraNumber: string) => {
      const groupName = cameraNumberToGroupName(cameraNumber);
      createGroup.mutate(
        { project_id: projectId, name: groupName },
        {
          onSuccess: (newGroup) => {
            updateCamera.mutate({
              id: cameraId,
              cameraPlanId,
              updates: { equipmentGroupID: newGroup.id },
            });
          },
        },
      );
    },
    [createGroup, updateCamera, projectId, cameraPlanId],
  );

  const handleAddEquipment = useCallback(
    (groupId: string, equipmentId: string) => {
      createAssignment.mutate({
        group_id: groupId,
        equipment_id: equipmentId,
        amount: 1,
        projectId,
      });
    },
    [createAssignment, projectId],
  );

  const handleAddTemporary = useCallback(
    async (groupId: string, name: string, columnKey?: string) => {
      const temp = await createTemporary.mutateAsync({
        project_id: projectId,
        name,
      });
      createAssignment.mutate({
        group_id: groupId,
        temporary_equipment_id: temp.id,
        amount: 1,
        ...(columnKey ? { camera_plan_column_key: columnKey } : {}),
        projectId,
      });
    },
    [createTemporary, createAssignment, projectId],
  );

  const handleRemoveAssignment = useCallback(
    (assignmentId: string) => {
      deleteAssignment.mutate({ id: assignmentId, projectId });
    },
    [deleteAssignment, projectId],
  );

  const handleSwapEquipment = useCallback(
    async (assignmentId: string, newEquipmentId: string) => {
      // Clear old instance assignments (they belong to the old equipment)
      const group = equipmentGroups.find((g) =>
        g.assignments.some((a) => a.id === assignmentId),
      );
      const assignment = group?.assignments.find((a) => a.id === assignmentId);
      if (assignment?.instance_assignments) {
        for (const ia of assignment.instance_assignments) {
          await unassignInstance.mutateAsync({ id: ia.id, projectId });
        }
      }
      updateAssignment.mutate({
        id: assignmentId,
        projectId,
        updates: {
          equipment_id: newEquipmentId,
          temporary_equipment_id: null,
        },
      });
    },
    [updateAssignment, unassignInstance, equipmentGroups, projectId],
  );

  const handleSwapTemporary = useCallback(
    async (assignmentId: string, tempName: string) => {
      // Clear old instance assignments
      const group = equipmentGroups.find((g) =>
        g.assignments.some((a) => a.id === assignmentId),
      );
      const assignment = group?.assignments.find((a) => a.id === assignmentId);
      if (assignment?.instance_assignments) {
        for (const ia of assignment.instance_assignments) {
          await unassignInstance.mutateAsync({ id: ia.id, projectId });
        }
      }
      const temp = await createTemporary.mutateAsync({
        project_id: projectId,
        name: tempName,
      });
      updateAssignment.mutate({
        id: assignmentId,
        projectId,
        updates: {
          temporary_equipment_id: temp.id,
          equipment_id: null,
        },
      });
    },
    [
      updateAssignment,
      unassignInstance,
      createTemporary,
      equipmentGroups,
      projectId,
    ],
  );

  const handleAssignInstance = useCallback(
    (
      assignmentId: string,
      instanceId: string,
      instancePreview: { id: string; identifier: string; status: string },
    ) => {
      assignInstance.mutate({
        assignmentId,
        instanceId,
        projectId,
        instancePreview,
      });
    },
    [assignInstance, projectId],
  );

  const handleUnassignInstance = useCallback(
    (instanceAssignmentId: string) => {
      unassignInstance.mutate({ id: instanceAssignmentId, projectId });
    },
    [unassignInstance, projectId],
  );

  const handleAddOperator = useCallback(
    async (cameraId: string, functionAssignmentId: string) => {
      await addOperator.mutateAsync({
        cameraId,
        functionAssignmentId,
        cameraPlanId,
      });
    },
    [addOperator, cameraPlanId],
  );

  const handleRemoveOperator = useCallback(
    (cameraFunctionAssignmentId: string) => {
      removeOperator.mutate({
        id: cameraFunctionAssignmentId,
        cameraPlanId,
      });
    },
    [removeOperator, cameraPlanId],
  );

  // Handle selecting a person from the fallback "all people" list
  const handleFallbackPersonSelect = useCallback(
    async (cameraId: string, personId: string, personName: string) => {
      const options: FunctionOption[] = cameraPositionDaysForDate.map(
        ({ series, day }) => {
          const currentCount = day.assignments?.length ?? 0;
          const requiredCount =
            day.required_count_override ?? series.required_count;
          return {
            series,
            day,
            hasOpenSpots: currentCount < requiredCount,
            currentCount,
            requiredCount,
          };
        },
      );

      const openOptions = options.filter((o) => o.hasOpenSpots);

      if (openOptions.length === 1) {
        // Auto-assign: create functionAssignment then link to camera
        const { day } = openOptions[0];
        const result = await createFunctionAssignment.mutateAsync({
          functionId: day.id,
          personId,
          projectId,
        });
        await addOperator.mutateAsync({
          cameraId,
          functionAssignmentId: result.data.id,
          cameraPlanId,
        });
      } else {
        // Show dialog: multiple options, no open spots, or no functions exist
        setFunctionDialogState({ personId, personName, cameraId, options });
      }
    },
    [
      cameraPositionDaysForDate,
      createFunctionAssignment,
      addOperator,
      projectId,
      cameraPlanId,
    ],
  );

  // Handle dialog selection of a specific function day
  const handleDialogFunctionSelect = useCallback(
    async (dayId: string, incrementCapacity: boolean) => {
      if (!functionDialogState) return;
      const { personId, cameraId } = functionDialogState;

      if (incrementCapacity) {
        const option = functionDialogState.options.find(
          (o) => o.day.id === dayId,
        );
        if (option) {
          await updateFunctionDay.mutateAsync({
            id: dayId,
            projectId,
            requiredCountOverride: option.requiredCount + 1,
          });
        }
      }

      const result = await createFunctionAssignment.mutateAsync({
        functionId: dayId,
        personId,
        projectId,
      });
      await addOperator.mutateAsync({
        cameraId,
        functionAssignmentId: result.data.id,
        cameraPlanId,
      });
      setFunctionDialogState(null);
    },
    [
      functionDialogState,
      updateFunctionDay,
      createFunctionAssignment,
      addOperator,
      projectId,
      cameraPlanId,
    ],
  );

  // Handle creating a new camera_position function series when none exist
  const handleCreateFunctionAndAssign = useCallback(async () => {
    if (!functionDialogState || !normalizedEventDate) return;
    const { personId, cameraId } = functionDialogState;

    // Create a new camera_position function series for the event date
    const series = await createFunctionSeries.mutateAsync({
      project_id: projectId,
      name: "Camera Position",
      required_count: 1,
      selected_days: [normalizedEventDate],
      system_tag: "camera_position",
    });

    const dayRow = series.days.find(
      (day) => day.function_date === normalizedEventDate,
    );
    if (!dayRow) return;

    const result = await createFunctionAssignment.mutateAsync({
      functionId: dayRow.id,
      personId,
      projectId,
    });
    await addOperator.mutateAsync({
      cameraId,
      functionAssignmentId: result.data.id,
      cameraPlanId,
    });
    setFunctionDialogState(null);
  }, [
    functionDialogState,
    normalizedEventDate,
    createFunctionSeries,
    createFunctionAssignment,
    addOperator,
    projectId,
    cameraPlanId,
  ]);

  if (cameras.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No cameras yet. Click "Add Camera" to get started.
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-x-auto">
      <table
        className="w-auto caption-bottom text-sm"
        data-testid="camera-list-table"
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-24 max-sm:sticky max-sm:left-0 max-sm:z-30 max-sm:w-12 max-sm:min-w-12 max-sm:bg-background max-sm:px-2">
              #
            </TableHead>
            <TableHead className="w-12">Icon</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Operators</TableHead>
            {equipmentColumns.length > 0 ? (
              <>
                {equipmentColumns.map((eqCol, index) => (
                  <Fragment key={eqCol.key}>
                    {index === transmissionColumnIndex && (
                      <TableHead className="w-28">Transmission</TableHead>
                    )}
                    <TableHead>
                      {getEquipmentColumnLabel(eqCol.label)}
                    </TableHead>
                  </Fragment>
                ))}
                {transmissionColumnIndex === equipmentColumns.length && (
                  <TableHead className="w-28">Transmission</TableHead>
                )}
              </>
            ) : (
              <>
                <TableHead className="w-28">Transmission</TableHead>
                <TableHead>Equipment</TableHead>
              </>
            )}
            {columns.map((col) => (
              <TableHead key={col.id}>{col.name}</TableHead>
            ))}
            <TableHead>Equipment Group</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {cameras.map((camera) => (
            <CameraListRow
              key={camera.id}
              camera={camera}
              columns={columns}
              cameraPlanId={cameraPlanId}
              projectId={projectId}
              equipmentGroups={equipmentGroups}
              functionAssignments={allFunctionAssignments}
              allLinkedFaIds={allLinkedFaIds}
              fallbackPeople={fallbackPeople}
              equipmentColumns={equipmentColumns}
              transmissionColumnIndex={transmissionColumnIndex}
              equipmentTagMap={equipmentTagMap}
              isSelected={selectedCameraId === camera.id}
              onSelect={() =>
                onSelectCamera?.(
                  camera.id === selectedCameraId ? null : camera.id,
                )
              }
              onAutoCreateGroup={() =>
                handleAutoCreateGroup(camera.id, camera.number)
              }
              onAddEquipment={handleAddEquipment}
              onAddTemporary={(groupId, name, columnKey) =>
                handleAddTemporary(groupId, name, columnKey)
              }
              onSwapEquipment={handleSwapEquipment}
              onSwapTemporary={handleSwapTemporary}
              onRemoveAssignment={handleRemoveAssignment}
              onAssignInstance={handleAssignInstance}
              onUnassignInstance={handleUnassignInstance}
              instancesByEquipmentId={instancesByEquipmentId}
              onAddOperator={(faId) => handleAddOperator(camera.id, faId)}
              onRemoveOperator={handleRemoveOperator}
              onFallbackPersonSelect={(personId, personName) =>
                handleFallbackPersonSelect(camera.id, personId, personName)
              }
            />
          ))}
        </TableBody>
      </table>

      {functionDialogState && (
        <SelectFunctionDialog
          open={!!functionDialogState}
          onOpenChange={(open) => !open && setFunctionDialogState(null)}
          personName={functionDialogState.personName}
          functionOptions={functionDialogState.options}
          onSelectFunction={handleDialogFunctionSelect}
          onCreateFunction={handleCreateFunctionAndAssign}
          isPending={
            createFunctionAssignment.isPending ||
            updateFunctionDay.isPending ||
            createFunctionSeries.isPending
          }
        />
      )}
    </div>
  );
}

function CameraListRow({
  camera,
  columns,
  cameraPlanId,
  projectId,
  equipmentGroups,
  functionAssignments,
  allLinkedFaIds,
  fallbackPeople,
  equipmentColumns,
  transmissionColumnIndex,
  equipmentTagMap,
  isSelected,
  onSelect,
  onAutoCreateGroup,
  onAddEquipment,
  onAddTemporary,
  onSwapEquipment,
  onSwapTemporary,
  onRemoveAssignment,
  onAssignInstance,
  onUnassignInstance,
  instancesByEquipmentId,
  onAddOperator,
  onRemoveOperator,
  onFallbackPersonSelect,
}: {
  camera: CameraWithEquipment;
  columns: CameraPlanColumn[];
  cameraPlanId: string;
  projectId: string;
  equipmentGroups: ReturnType<typeof useProjectEquipmentGroups>["data"] & {};
  functionAssignments: (FunctionAssignment & {
    functionName: string;
    functionDate: string;
  })[];
  allLinkedFaIds: Set<string>;
  fallbackPeople: PersonWithCompany[];
  equipmentColumns: CameraPlanEquipmentColumn[];
  transmissionColumnIndex: number;
  equipmentTagMap: Map<string, string[]>;
  isSelected: boolean;
  onSelect: () => void;
  onAutoCreateGroup: () => void;
  onAddEquipment: (groupId: string, equipmentId: string) => void;
  onAddTemporary: (groupId: string, name: string, columnKey?: string) => void;
  onSwapEquipment: (assignmentId: string, newEquipmentId: string) => void;
  onSwapTemporary: (assignmentId: string, tempName: string) => void;
  onRemoveAssignment: (assignmentId: string) => void;
  onAssignInstance: (
    assignmentId: string,
    instanceId: string,
    instancePreview: { id: string; identifier: string; status: string },
  ) => void;
  onUnassignInstance: (instanceAssignmentId: string) => void;
  instancesByEquipmentId: Record<
    string,
    { id: string; identifier: string; status: string }[]
  >;
  onAddOperator: (functionAssignmentId: string) => Promise<void>;
  onRemoveOperator: (cameraFunctionAssignmentId: string) => void;
  onFallbackPersonSelect: (
    personId: string,
    personName: string,
  ) => Promise<void>;
}) {
  const [editingField, setEditingField] = useState<string | null>(null);

  const updateCamera = useUpdateCamera();
  const deleteCamera = useDeleteCamera();
  const updateGroup = useUpdateEquipmentGroup();

  const hasOperators = (camera.cameraFunctionAssignments ?? []).length > 0;
  const isUnmanned = camera.customValues?.unmanned === true;

  const selectedGroup = useMemo(
    () => equipmentGroups.find((g) => g.id === camera.equipmentGroupID) ?? null,
    [equipmentGroups, camera.equipmentGroupID],
  );

  const numberDraft = useDraftField({
    value: camera.number,
    toShared: normalizeCameraNumber,
    onCommit: async (normalizedNumber) => {
      const oldGroupName = cameraNumberToGroupName(camera.number);
      if (
        selectedGroup &&
        selectedGroup.name === oldGroupName &&
        normalizedNumber !== camera.number
      ) {
        updateGroup.mutate({
          id: selectedGroup.id,
          projectId,
          updates: { name: cameraNumberToGroupName(normalizedNumber) },
        });
      }
      await updateCamera.mutateAsync({
        id: camera.id,
        cameraPlanId,
        updates: { number: normalizedNumber },
      });
      setEditingField(null);
    },
  });

  const descriptionDraft = useDraftField<string, string | null>({
    value: camera.description,
    fromShared: (value) => value ?? "",
    toShared: (draft) => draft || null,
    onCommit: async (description) => {
      await updateCamera.mutateAsync({
        id: camera.id,
        cameraPlanId,
        updates: { description },
      });
      setEditingField(null);
    },
  });

  // Available assignments not yet linked to any camera
  const availableAssignments = useMemo(
    () => functionAssignments.filter((fa) => !allLinkedFaIds.has(fa.id)),
    [functionAssignments, allLinkedFaIds],
  );

  // Operator search options: assigned people first, then fallback people
  interface OperatorOption {
    value: string;
    label: string;
    subtitle?: string;
    pool: "assigned" | "fallback";
  }
  const operatorOptions = useMemo<OperatorOption[]>(() => {
    const assignmentOpts: OperatorOption[] = availableAssignments.map((fa) => ({
      value: `fa:${fa.id}`,
      label: fa.person
        ? `${fa.person.first_name} ${fa.person.last_name}`
        : "Unknown",
      subtitle: fa.functionName,
      pool: "assigned",
    }));
    const peopleOpts: OperatorOption[] = fallbackPeople.map((p) => ({
      value: `person:${p.id}`,
      label: `${p.first_name} ${p.last_name}`,
      subtitle: p.companies?.name ?? undefined,
      pool: "fallback",
    }));
    return [...assignmentOpts, ...peopleOpts];
  }, [availableAssignments, fallbackPeople]);

  const [operatorSearch, setOperatorSearch] = useState("");
  const [operatorPopoverOpen, setOperatorPopoverOpen] = useState(false);

  // Progressive disclosure: show assigned operators first, fall back to all
  // people only when no assigned operators match or none exist
  const filteredOperatorOptions = useMemo(() => {
    const search = operatorSearch.trim().toLowerCase();
    const matches = (o: OperatorOption) =>
      !search ||
      `${o.label} ${o.subtitle ?? ""}`.toLowerCase().includes(search);

    const assigned = operatorOptions.filter(
      (o) => o.pool === "assigned" && matches(o),
    );
    if (assigned.length > 0) return assigned;

    return operatorOptions.filter((o) => o.pool === "fallback" && matches(o));
  }, [operatorOptions, operatorSearch]);

  const handleInlineEdit = (field: "number" | "description") => {
    if (field === "number") numberDraft.start();
    if (field === "description") descriptionDraft.start();
    setEditingField(field);
  };

  const handleInlineCancel = () => {
    numberDraft.cancel();
    descriptionDraft.cancel();
    setEditingField(null);
  };

  const handleNumberCommit = async () => {
    await numberDraft.commit();
    setEditingField(null);
  };

  const handleDescriptionCommit = async () => {
    await descriptionDraft.commit();
    setEditingField(null);
  };

  const handleCustomValueChange = (
    columnId: string,
    value: string | boolean | string[],
  ) => {
    const newValues = { ...camera.customValues, [columnId]: value };
    updateCamera.mutate({
      id: camera.id,
      cameraPlanId,
      updates: { customValues: newValues },
    });
  };

  const handleTransmissionChange = (value: string[]) => {
    handleCustomValueChange(TRANSMISSION_CUSTOM_VALUE_KEY, value);
  };

  const handleBondingEquipmentSelected = () => {
    if (
      normalizeTransmissionValue(
        camera.customValues[TRANSMISSION_CUSTOM_VALUE_KEY],
      ).length === 0
    ) {
      handleTransmissionChange(["Bonding"]);
    }
  };

  const handleSetUnmanned = (value: boolean) => {
    updateCamera.mutate({
      id: camera.id,
      cameraPlanId,
      updates: {
        customValues: {
          ...camera.customValues,
          unmanned: value,
        },
      },
    });
  };

  const handleIconChange = (icon: CameraIcon) => {
    updateCamera.mutate({ id: camera.id, cameraPlanId, updates: { icon } });
  };

  const handleEquipmentGroupChange = (groupId: string | null) => {
    updateCamera.mutate({
      id: camera.id,
      cameraPlanId,
      updates: { equipmentGroupID: groupId },
    });
  };

  return (
    <TableRow
      className={isSelected ? "bg-primary/10" : "cursor-pointer"}
      onClick={onSelect}
      data-testid={`camera-row-${camera.id}`}
    >
      <TableCell
        className={cn(
          "font-mono font-medium",
          "max-sm:sticky max-sm:left-0 max-sm:z-20 max-sm:w-12 max-sm:min-w-12 max-sm:px-2",
          isSelected ? "max-sm:bg-primary/10" : "max-sm:bg-background",
        )}
      >
        {editingField === "number" ? (
          <Input
            value={numberDraft.draft}
            onChange={(e) => numberDraft.setDraft(e.target.value)}
            onBlur={() => void handleNumberCommit()}
            onKeyDown={getDraftFieldKeyDownHandlers({
              commit: handleNumberCommit,
              cancel: handleInlineCancel,
            })}
            className="h-7 w-20 text-sm"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="cursor-pointer hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              handleInlineEdit("number");
            }}
          >
            <span className="hidden sm:inline">
              {formatCameraNumber(camera.number)}
            </span>
            <span className="sm:hidden">
              {formatCompactCameraNumber(camera.number)}
            </span>
          </span>
        )}
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        <CameraIconPicker value={camera.icon} onChange={handleIconChange} />
      </TableCell>

      <TableCell>
        {editingField === "description" ? (
          <Input
            value={descriptionDraft.draft}
            onChange={(e) => descriptionDraft.setDraft(e.target.value)}
            onBlur={() => void handleDescriptionCommit()}
            onKeyDown={getDraftFieldKeyDownHandlers({
              commit: handleDescriptionCommit,
              cancel: handleInlineCancel,
            })}
            className="h-7 text-sm text-white"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="cursor-pointer hover:underline text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleInlineEdit("description");
            }}
          >
            {camera.description || "—"}
          </span>
        )}
      </TableCell>

      {/* Operators (multi-operator via junction table) */}
      <TableCell
        onClick={(e) => e.stopPropagation()}
        data-testid={`camera-operators-${camera.id}`}
      >
        <div className="flex flex-wrap items-center gap-1 min-w-[140px]">
          {(camera.cameraFunctionAssignments ?? []).map((cfa) => (
            <Badge
              key={cfa.id}
              variant="secondary"
              className="gap-1 text-xs pr-1"
              data-testid={`camera-operator-${cfa.id}`}
            >
              {cfa.functionAssignment?.person
                ? `${cfa.functionAssignment.person.firstName} ${cfa.functionAssignment.person.lastName}`
                : "—"}
              <button
                className="ml-0.5 hover:text-destructive"
                onClick={() => onRemoveOperator(cfa.id)}
                data-testid={`camera-remove-operator-${cfa.id}`}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {operatorOptions.length > 0 && !isUnmanned && (
            <Popover
              open={operatorPopoverOpen}
              onOpenChange={(open) => {
                setOperatorPopoverOpen(open);
                if (!open) setOperatorSearch("");
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  data-testid={`camera-add-operator-${camera.id}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search operators..."
                    value={operatorSearch}
                    onValueChange={setOperatorSearch}
                    data-testid={`camera-add-operator-${camera.id}-search`}
                  />
                  <CommandList>
                    <CommandEmpty>No people found.</CommandEmpty>
                    <CommandGroup>
                      {filteredOperatorOptions.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          data-testid={`camera-add-operator-option-${option.value}`}
                          onSelect={async () => {
                            const val = option.value;
                            if (val.startsWith("fa:")) {
                              if (isUnmanned) handleSetUnmanned(false);
                              await onAddOperator(val.slice(3));
                            } else if (val.startsWith("person:")) {
                              const personId = val.slice(7);
                              const person = fallbackPeople.find(
                                (p) => p.id === personId,
                              );
                              const name = person
                                ? `${person.first_name} ${person.last_name}`
                                : "";
                              if (isUnmanned) handleSetUnmanned(false);
                              await onFallbackPersonSelect(personId, name);
                            }
                            setOperatorPopoverOpen(false);
                            setOperatorSearch("");
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-col">
                            <span>{option.label}</span>
                            {option.subtitle && (
                              <span className="text-xs text-muted-foreground">
                                {option.subtitle}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          {!hasOperators && (
            <>
              {isUnmanned ? (
                <Badge
                  variant="outline"
                  className="gap-1 pr-1"
                  data-testid={`camera-unmanned-${camera.id}`}
                >
                  Unmanned
                  <button
                    className="ml-0.5 hover:text-destructive"
                    onClick={() => handleSetUnmanned(false)}
                    data-testid={`camera-clear-unmanned-${camera.id}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleSetUnmanned(true)}
                  data-testid={`camera-mark-unmanned-${camera.id}`}
                >
                  Mark unmanned
                </Button>
              )}
            </>
          )}
        </div>
      </TableCell>

      {/* Equipment columns — either dynamic filtered columns or a single generic column */}
      {equipmentColumns.length > 0 ? (
        <>
          {equipmentColumns.map((eqCol, index) => {
            // Filter assignments: equipment by matching tags, temporary by column key
            const tagSet = new Set(eqCol.tagIds);
            const filtered = selectedGroup
              ? selectedGroup.assignments.filter((a) => {
                  if (a.equipment_id) {
                    const tags = equipmentTagMap.get(a.equipment_id) ?? [];
                    return tags.some((t) => tagSet.has(t));
                  }
                  if (a.temporary_equipment_id) {
                    return a.camera_plan_column_key === eqCol.key;
                  }
                  return false;
                })
              : [];
            const singleAssignment = filtered.length > 0 ? filtered[0] : null;

            // Compute available instances for this assignment's equipment
            const eqId = singleAssignment?.equipment_id;
            const allInstances = eqId
              ? (instancesByEquipmentId[eqId] ?? [])
              : [];
            const assignedInstanceIds = new Set(
              singleAssignment?.instance_assignments?.map(
                (ia) => ia.instance_id,
              ) ?? [],
            );
            const availableInstances = allInstances.filter(
              (inst) =>
                !assignedInstanceIds.has(inst.id) && inst.status === "active",
            );
            const isBondingColumn = isBondingEquipmentColumn(eqCol.label);

            return (
              <Fragment key={eqCol.key}>
                {index === transmissionColumnIndex && (
                  <TransmissionTableCell
                    value={normalizeTransmissionValue(
                      camera.customValues[TRANSMISSION_CUSTOM_VALUE_KEY],
                    )}
                    onChange={handleTransmissionChange}
                  />
                )}
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {selectedGroup ? (
                    <CameraEquipmentInline
                      assignment={singleAssignment}
                      onAddEquipment={(eqId) => {
                        if (isBondingColumn) handleBondingEquipmentSelected();
                        onAddEquipment(selectedGroup.id, eqId);
                      }}
                      onAddTemporary={(name) =>
                        onAddTemporary(selectedGroup.id, name, eqCol.key)
                      }
                      onSwapEquipment={(assignmentId, newEquipmentId) => {
                        if (isBondingColumn) handleBondingEquipmentSelected();
                        onSwapEquipment(assignmentId, newEquipmentId);
                      }}
                      onSwapTemporary={onSwapTemporary}
                      onRemoveAssignment={onRemoveAssignment}
                      onAssignInstance={onAssignInstance}
                      onUnassignInstance={onUnassignInstance}
                      availableInstances={availableInstances}
                      tagIds={eqCol.tagIds}
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TableCell>
              </Fragment>
            );
          })}
          {transmissionColumnIndex === equipmentColumns.length && (
            <TransmissionTableCell
              value={normalizeTransmissionValue(
                camera.customValues[TRANSMISSION_CUSTOM_VALUE_KEY],
              )}
              onChange={handleTransmissionChange}
            />
          )}
        </>
      ) : (
        <>
          <TransmissionTableCell
            value={normalizeTransmissionValue(
              camera.customValues[TRANSMISSION_CUSTOM_VALUE_KEY],
            )}
            onChange={handleTransmissionChange}
          />
          <TableCell onClick={(e) => e.stopPropagation()}>
            {selectedGroup ? (
              (() => {
                const singleAssignment =
                  selectedGroup.assignments.length > 0
                    ? selectedGroup.assignments[0]
                    : null;
                const eqId = singleAssignment?.equipment_id;
                const allInstances = eqId
                  ? (instancesByEquipmentId[eqId] ?? [])
                  : [];
                const assignedInstanceIds = new Set(
                  singleAssignment?.instance_assignments?.map(
                    (ia) => ia.instance_id,
                  ) ?? [],
                );
                const availableInstances = allInstances.filter(
                  (inst) =>
                    !assignedInstanceIds.has(inst.id) &&
                    inst.status === "active",
                );
                return (
                  <CameraEquipmentInline
                    assignment={singleAssignment}
                    onAddEquipment={(eqId) =>
                      onAddEquipment(selectedGroup.id, eqId)
                    }
                    onAddTemporary={(name) =>
                      onAddTemporary(selectedGroup.id, name)
                    }
                    onSwapEquipment={onSwapEquipment}
                    onSwapTemporary={onSwapTemporary}
                    onRemoveAssignment={onRemoveAssignment}
                    onAssignInstance={onAssignInstance}
                    onUnassignInstance={onUnassignInstance}
                    availableInstances={availableInstances}
                  />
                );
              })()
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </TableCell>
        </>
      )}

      {columns.map((col) => (
        <TableCell key={col.id} onClick={(e) => e.stopPropagation()}>
          <CustomColumnCell
            column={col}
            value={
              Array.isArray(camera.customValues[col.id])
                ? undefined
                : (camera.customValues[col.id] as string | boolean | undefined)
            }
            onChange={(v) => handleCustomValueChange(col.id, v)}
          />
        </TableCell>
      ))}

      {/* Equipment Group */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <EquipmentGroupSelect
          groups={equipmentGroups}
          value={camera.equipmentGroupID}
          onChange={handleEquipmentGroupChange}
          onCreateGroup={onAutoCreateGroup}
        />
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => deleteCamera.mutate({ id: camera.id, cameraPlanId })}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function CustomColumnCell({
  column,
  value,
  onChange,
}: {
  column: CameraPlanColumn;
  value: string | boolean | undefined;
  onChange: (value: string | boolean | string[]) => void;
}) {
  const textDraft = useDraftField<string, string | boolean | undefined>({
    value,
    fromShared: (shared) => (typeof shared === "string" ? shared : ""),
    toShared: (draft) => draft,
    onCommit: onChange,
  });

  if (column.columnType === "checkbox") {
    return (
      <Checkbox
        checked={!!value}
        onCheckedChange={(checked) => onChange(!!checked)}
      />
    );
  }

  if (column.columnType === "select") {
    return (
      <Select
        value={(value as string) || ""}
        onValueChange={(v) => onChange(v)}
      >
        <SelectTrigger className="h-7 text-sm w-full min-w-[80px]">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {column.options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Text
  if (textDraft.isEditing) {
    return (
      <Input
        value={textDraft.draft}
        onChange={(e) => textDraft.setDraft(e.target.value)}
        onBlur={() => void textDraft.commit()}
        onKeyDown={getDraftFieldKeyDownHandlers(textDraft)}
        className="h-7 text-sm"
        autoFocus
      />
    );
  }

  return (
    <span
      className="cursor-pointer hover:underline text-sm"
      onClick={textDraft.start}
    >
      {(value as string) || "—"}
    </span>
  );
}

function TransmissionMultiSelect({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const handleToggle = (option: string) => {
    onChange(
      value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-7 w-[112px] justify-between px-2 py-1 text-xs"
          title={value.join(", ")}
        >
          <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            {value.length > 0 ? (
              value.map((item) => (
                <span key={item} className="w-full truncate text-left">
                  {item}
                </span>
              ))
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {TRANSMISSION_OPTIONS.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => handleToggle(option)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value.includes(option) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TransmissionTableCell({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <TableCell className="w-28" onClick={(e) => e.stopPropagation()}>
      <TransmissionMultiSelect value={value} onChange={onChange} />
    </TableCell>
  );
}

function normalizeTransmissionValue(
  value: string | boolean | string[] | undefined,
): string[] {
  if (Array.isArray(value)) {
    return value.filter((item) => TRANSMISSION_OPTIONS.includes(item));
  }
  if (typeof value === "string" && TRANSMISSION_OPTIONS.includes(value)) {
    return [value];
  }
  return [];
}

function zeroDateToDateString(value: ZeroDateValue | undefined): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return new Date(value).toISOString().slice(0, 10);
  }
  return value.slice(0, 10);
}

function getEquipmentColumnLabel(label: string): string {
  return isBondingEquipmentColumn(label) ? "Bonding" : label;
}

function isBondingEquipmentColumn(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return normalized === "bonding" || normalized === "transmission";
}

function getTransmissionColumnIndex(
  equipmentColumns: CameraPlanEquipmentColumn[],
): number {
  const afterCameraIndex = equipmentColumns.findIndex(
    (column) => column.label.trim().toLowerCase() === "camera",
  );
  if (afterCameraIndex >= 0) return afterCameraIndex + 1;

  const beforeBondingIndex = equipmentColumns.findIndex((column) => {
    return isBondingEquipmentColumn(column.label);
  });
  if (beforeBondingIndex >= 0) return beforeBondingIndex;

  return 0;
}

/** Derive an equipment group name from a camera number, e.g. "3" → "CAM03". */
function cameraNumberToGroupName(cameraNumber: string): string {
  const num = cameraNumber.match(/\d+$/);
  if (num) {
    return `CAM${num[0].padStart(2, "0")}`;
  }
  return `CAM${cameraNumber}`;
}

function formatCameraNumber(cameraNumber: string): string {
  return cameraNumber.toUpperCase().startsWith("CAM")
    ? cameraNumber
    : `CAM${cameraNumber}`;
}

function formatCompactCameraNumber(cameraNumber: string): string {
  return cameraNumber.trim().replace(/^CAM/i, "").trim() || cameraNumber;
}

function normalizeCameraNumber(cameraNumber: string): string {
  return cameraNumber.trim().replace(/^CAM/i, "").trim();
}
